import { z } from 'zod';

import type {
    JamForm,
    JamFormStepProduct,
} from '../types/jamForm';
import type { JobAdAttributes } from '../types/jobAd';
import { useJamForm } from './useJamForm';
import { useJamFormSteps } from './useJamFormSteps';
import type { JamServiceResponse } from './useJamService';
import type { JamServiceJobAdsSetup } from './useJamServiceJobAds';

export const zJamFormPackage = z.enum(['BASIC', 'PLUS-STARTER', 'PLUS', 'PREMIUM', 'APPRENTICE', 'STANDARD', 'PRO', 'BOOST']);

export const zJamFormProduct = z
    .object({
        product: zJamFormPackage,
    });

export const jamFormStepProductValidationErrorsMap: { [key: string]: string } = {
    'attributes.package': 'product',
};

export const jamFormStepProductServiceSetup: JamServiceJobAdsSetup<JamFormStepProduct> =
    {
        inbound: (data: JamFormStepProduct): JobAdAttributes => {
            return {
                layoutSettingsBackgroundColor:
                data.layoutSettings?.layoutSettingsBackgroundColor,
                layoutSettingsHeaderImages: data.layoutSettings?.layoutSettingsHeaderImages,
                layoutSettingsHeadlineColor: data.layoutSettings?.layoutSettingsHeadlineColor,
                layoutSettingsListIconColor: data.layoutSettings?.layoutSettingsListIconColor,
                layoutSettingsListIconStyle: data.layoutSettings?.layoutSettingsListIconStyle,
                layoutSettingsPhotoGalleryImages:
                data.layoutSettings?.layoutSettingsPhotoGalleryImages,
                layoutSettingsPhotoGalleryPresentation:
                data.layoutSettings?.layoutSettingsPhotoGalleryPresentation,
                layoutSettingsTextColor: data.layoutSettings?.layoutSettingsTextColor,
                layoutSettingsVideo: data.layoutSettings?.layoutSettingsVideo,
                package:
                    data.product === 'PLUS-STARTER' ? 'PLUS' : data.product,
            };
        },
        outbound: (attributes: JobAdAttributes): JamFormStepProduct => {
            return {
                product: attributes.package ?? 'BASIC',
            };
        },
        validationErrorsMap: jamFormStepProductValidationErrorsMap,
    };

export const jamFormStepProductDefaultProduct: JamFormStepProduct = {
    product: 'BASIC',
};

export const useJamFormStepProduct = (defaultData?: JamFormStepProduct) => {
    const { data, errors, hasError, submit, validate } =
        useJamForm<JamFormStepProduct>({
            defaultData: {
                ...structuredClone(jamFormStepProductDefaultProduct),
                ...defaultData,
            },
            validationRules: [zJamFormProduct],
        });

    const update = async (
        id: number,
        nextPage?: string,
        onSuccess?: (response: JamServiceResponse<JamForm>) => void,
    ) => {
        await submit(
            `/api/manage-job-ads/shared/job-ads/${id}/step-product`,
            'patch',
            useJamFormSteps().getAjaxSetup(nextPage, onSuccess) as any,
        );
    };

    return {
        data,
        errors,
        hasError,
        submit,
        update,
        validate,
    };
};
